/**
 * 화면 코드가 보는 두 가지 — **매니페스트**와 **문구**. (docs/decisions/0008)
 *
 * ─── 왜 컨텍스트가 web-sdk 에 있는가 ───
 * 이 둘은 코어 화면과 vertical 화면이 **똑같이** 필요로 한다. `core-web` 에 두면
 * vertical 이 코어 구현을 import 해야 하고(`verticals-import-sdk-only` 가 막는다),
 * 그렇다고 vertical 용 사본을 만들면 같은 컨텍스트가 둘이 되어 Provider 하나만 감싼 트리에서
 * **한쪽이 조용히 기본값을 읽는다.** 그래서 SDK 가 소유하고 core-web 이 그것을 쓴다
 * (core-web → web-sdk 방향 하나. 반대 방향이 생기면 `no-circular` 이 잡는다).
 *
 * ─── 기본값을 두지 않는다 ───
 * Provider 없이 훅을 부르면 **던진다.** React 컨텍스트의 기본값은 "빈 매니페스트"가 되고,
 * 빈 매니페스트는 "보이는 화면이 0개인 신규 직원"과 구분되지 않는다 —
 * 배선 사고가 정상 상태로 위장되는 형태이고, `resolveSurface` 가 빈 카탈로그에 던지는 이유와 같다.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { renderMessage } from '@worhythm/contracts';
import type { MessageDictionary, SurfaceManifest } from '@worhythm/contracts';

/*
 * `MessageDictionary` 는 **여기 없다.** 2026-09-02 에 `packages/contracts` 로 옮겼다 —
 * 사전이 `session.bootstrap` 의 응답 필드가 되면서 서버와 브라우저 **두 당사자의 계약**이
 * 됐고, 서버가 만드는 것을 브라우저 패키지가 소유할 수 없기 때문이다.
 * 여기서 재export 하지도 않는다: 이름이 둘이 되면 다음 사람이 어느 쪽을 import 할지 고르게
 * 되고, 고를 수 있다는 것이 곧 갈라질 수 있다는 뜻이다(`STAMP_HEADERS` 와 같은 규약).
 */

export interface WorhythmSurfaceValue {
  readonly manifest: SurfaceManifest;
  readonly messages: MessageDictionary;
}

const SurfaceContext = createContext<WorhythmSurfaceValue | null>(null);

export interface SurfaceProviderProps extends WorhythmSurfaceValue {
  children: ReactNode;
}

export function SurfaceProvider({ manifest, messages, children }: SurfaceProviderProps) {
  return <SurfaceContext.Provider value={{ manifest, messages }}>{children}</SurfaceContext.Provider>;
}

function useSurfaceContext(): WorhythmSurfaceValue {
  const v = useContext(SurfaceContext);
  if (!v) {
    throw new Error(
      'SurfaceProvider 밖에서 화면 훅을 불렀다. 매니페스트 없이 그린 화면은 링크·제목·빵부스러기를 ' +
        '모두 잘못 그리므로 여기서 멈춘다 — 기본값을 주면 그 상태가 "화면이 0개인 사용자"로 위장된다.',
    );
  }
  return v;
}

/** 이 사용자에게 **보이는** 화면 전부. granted 필터는 서버가 이미 했다(클라이언트는 필터하지 않는다). */
export function useManifest(): SurfaceManifest {
  return useSurfaceContext().manifest;
}

/**
 * 문구를 렌더한다.
 *
 * **없는 키는 키 자체를 그린다.** 마지막 조각만 뽑아 그럴듯하게 보여 주는 폴백은
 * "번역이 빠졌다"를 "번역이 있다"로 위장한다 — 화면에 `pay.screen.payroll.runs` 가 그대로
 * 뜨는 편이 시끄럽고, 시끄러운 것이 이 리포의 선택이다. (사전 생성과 존재 검사는 §구현 순서 11)
 *
 * `params` 는 `{name}` 자리를 채운다. 에러 레지스트리의 `params` 가 그대로 들어온다.
 */
export function useMessage(): (key: string, params?: Readonly<Record<string, unknown>>) => string {
  const { messages } = useSurfaceContext();
  // 규칙은 `packages/contracts` 가 소유한다 — 서버도 같은 함수로 렌더한다(`ctx.t`).
  // 여기 다시 적으면 파라미터 채우기가 한쪽에만 있는 날이 오고, 그때 같은 급여가
  // 화면과 PDF 에서 다르게 보인다.
  return (key, params) => renderMessage(messages, key, params);
}
